import { useNavigate } from 'react-router-dom'
import { coachSeedForPattern } from '@pelvia/shared/lib/patient/coachSeed'
import { painColor } from '@pelvia/shared/lib/patient/painColors'
import type { PainPattern, InsightViz } from '@pelvia/shared/hooks/patient/usePainPatterns'

const CATEGORY_LABEL: Record<string, string> = {
  cycle: 'Cycle',
  sleep: 'Sleep',
  stress: 'Wellbeing',
  diet: 'Diet',
  meds: 'Medication',
  biometrics: 'Wearable',
  symptom: 'Symptoms',
  trend: 'Trend',
}

const CONFIDENCE_LABEL: Record<string, string> = {
  strong: 'Strong signal',
  moderate: 'Moderate signal',
  emerging: 'Emerging',
}

/**
 * One enriched "Your patterns" insight. Shared by the dashboard card and the
 * full Pattern analysis page. Clicking opens the AI coach seeded with the
 * insight, matching the prior pattern-card behaviour.
 */
export default function InsightCard({
  pattern,
  index = 0,
}: {
  pattern: PainPattern
  index?: number
}) {
  const navigate = useNavigate()
  const open = () =>
    navigate('/patient/coach', {
      state: { seedPrompt: coachSeedForPattern(pattern.title, pattern.body) },
    })

  const category = pattern.category ?? 'trend'
  const ev = pattern.evidence

  return (
    <article
      className={`insight-card insight-${pattern.severity}`}
      role="button"
      tabIndex={0}
      style={{ ['--insight-delay' as string]: `${index * 70}ms` }}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
    >
      <span className="insight-stripe" aria-hidden />
      <div className="insight-main">
        <div className="insight-head">
          <span className={`insight-chip insight-chip-${category}`}>
            {CATEGORY_LABEL[category] ?? category}
          </span>
          {pattern.confidence && (
            <span className={`insight-confidence insight-confidence-${pattern.confidence}`}>
              {CONFIDENCE_LABEL[pattern.confidence] ?? pattern.confidence}
            </span>
          )}
        </div>

        <div className="insight-title">{pattern.title}</div>
        <div className="insight-body">{pattern.body}</div>

        {pattern.viz && <InsightMiniViz viz={pattern.viz} />}

        <div className="insight-foot">
          {ev && (
            <span className="insight-evidence">
              <strong>{ev.stat}</strong>
              <span className="insight-evidence-detail"> · {ev.detail}</span>
            </span>
          )}
          {pattern.action && (
            <span className="insight-action">{pattern.action.label} →</span>
          )}
        </div>
      </div>
    </article>
  )
}

// ---- Mini visualizations ----------------------------------------------------

function InsightMiniViz({ viz }: { viz: InsightViz }) {
  if (viz.kind === 'bars') {
    const vals = viz.data.filter((d) => d.value != null)
    if (vals.length === 0) return null
    const max = Math.max(10, ...vals.map((d) => d.value))
    return (
      <div className="insight-viz insight-viz-bars" role="img" aria-label="Average pain by group">
        {vals.map((d, i) => (
          <div key={i} className="insight-bar-col">
            <span className="insight-bar-val">{d.value}</span>
            <div
              className="insight-bar"
              style={{ height: `${Math.max(6, (d.value / max) * 52)}px`, background: painColor(d.value) }}
            />
            <span className="insight-bar-label">{d.label}</span>
          </div>
        ))}
      </div>
    )
  }

  if (viz.kind === 'delta') {
    const w = viz.with
    const wo = viz.without
    if (w == null || wo == null) return null
    const max = Math.max(10, w, wo)
    return (
      <div className="insight-viz insight-viz-delta" role="img" aria-label="Pain on vs off days">
        <div className="insight-delta-col">
          <span className="insight-bar-val">{w}</span>
          <div className="insight-bar" style={{ height: `${Math.max(6, (w / max) * 52)}px`, background: painColor(w) }} />
          <span className="insight-bar-label">on</span>
        </div>
        <div className="insight-delta-col">
          <span className="insight-bar-val">{wo}</span>
          <div className="insight-bar" style={{ height: `${Math.max(6, (wo / max) * 52)}px`, background: painColor(wo) }} />
          <span className="insight-bar-label">off</span>
        </div>
      </div>
    )
  }

  if (viz.kind === 'sparkline') {
    const data = viz.data.filter((n) => n != null)
    if (data.length < 2) return null
    const min = Math.min(...data)
    const max = Math.max(...data)
    const span = max - min || 1
    const W = 120
    const H = 32
    const pts = data
      .map((v, i) => {
        const x = (i / (data.length - 1)) * W
        const y = H - ((v - min) / span) * H
        return `${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
    return (
      <div className="insight-viz insight-viz-spark">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-label="Weekly pain trend">
          <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    )
  }

  // corr: a centered strength bar — green = protective (r<0), red = aggravating (r>0)
  const r = viz.r
  const pct = Math.min(100, Math.abs(r) * 100)
  const protective = r < 0
  return (
    <div className="insight-viz insight-viz-corr" role="img" aria-label="Correlation strength">
      <div className="insight-corr-track">
        <div
          className={`insight-corr-fill ${protective ? 'is-protective' : 'is-aggravating'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="insight-corr-label">{protective ? 'protective' : 'aggravating'}</span>
    </div>
  )
}
